import { useEffect, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useChainNow } from '../../hooks/useChainClock.tsx';
import { RollText } from './depth.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { fmtDuration } from '../../lib/format.ts';
import { Icon, type IconName } from './Icon.tsx';
import { Coin, type Token } from './coins.tsx';

type Variant = 'primary' | 'secondary' | 'ghost';

export function Button({
  variant = 'primary',
  size,
  block,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm'; block?: boolean }) {
  const cls = ['btn', `btn-${variant}`, size === 'sm' ? 'btn-sm' : '', block ? 'btn-block' : '', className].filter(Boolean).join(' ');
  return <button type="button" className={cls} {...rest} />;
}

export function Card({ title, children, highlight, className = '' }: { title?: ReactNode; children: ReactNode; highlight?: boolean; className?: string }) {
  return (
    <section className={`card${highlight ? ' highlight' : ''} ${className}`}>
      {title ? <h3>{title}</h3> : null}
      {children}
    </section>
  );
}

/** A card title led by a glowing icon badge (use with `className="badge-card"` on the card). */
export function BadgeTitle({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <>
      <span className="icon-badge" aria-hidden="true">
        <Icon name={icon} width={18} height={18} />
      </span>
      {children}
    </>
  );
}

export function SectionHead({ eyebrow, title, right }: { eyebrow: string; title: ReactNode; right?: ReactNode }) {
  return (
    <div className="section-head">
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h2>{title}</h2>
      </div>
      {right}
    </div>
  );
}

/** A value that is still loading renders as a shimmer; an unavailable one as an em dash. */
export function Value({ children, loading }: { children: ReactNode; loading?: boolean }) {
  if (loading) return <span className="skeleton" aria-label="loading" />;
  return <>{children}</>;
}

export function StatTile({ label, value, sub, loading, coin }: { label: ReactNode; value: ReactNode; sub?: ReactNode; loading?: boolean; coin?: Token }) {
  return (
    <div className="tile" data-lean>
      {coin ? <Coin token={coin} size={28} /> : null}
      <span className="label">{label}</span>
      <span className="value">
        <Value loading={loading}>{value}</Value>
      </span>
      {sub ? <span className="sub">{sub}</span> : null}
    </div>
  );
}

export function Pill({ tone = 'accent', children }: { tone?: 'accent' | 'green' | 'red' | 'amber' | 'muted'; children: ReactNode }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: ReactNode }[]; onChange(v: T): void; label: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Progress({ value, tone }: { value: number; tone?: 'green' }) {
  const pct = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0)) * 100;
  return (
    <div className={`progress${tone ? ` ${tone}` : ''}`} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Countdown({ to }: { to: number | undefined }) {
  const now = useChainNow();
  if (to === undefined) return <span className="skeleton" />;
  return (
    <span className="num">
      <RollText text={fmtDuration(to - now)} />
    </span>
  );
}

export function TokenChip({ symbol, icon }: { symbol: string; icon?: string }) {
  return (
    <span className="token-chip">
      {icon ? <img src={icon} alt="" /> : null}
      {symbol}
    </span>
  );
}

export function AmountInput({
  label,
  value,
  onChange,
  balance,
  onMax,
  token,
  icon,
  hint,
  id,
  chip,
}: {
  label: ReactNode;
  value: string;
  onChange(v: string): void;
  balance?: ReactNode;
  onMax?: () => void;
  token: string;
  icon?: string;
  hint?: ReactNode;
  id: string;
  chip?: ReactNode; // in place of the token chip, e.g. a token menu
}) {
  const { t } = useI18n();
  return (
    <div className="field">
      <div className="field-top">
        <label htmlFor={id}>{label}</label>
        {balance !== undefined ? (
          <span>
            {t('common.balance')}: <span className="num">{balance}</span>
          </span>
        ) : null}
      </div>
      <div className="amount">
        <input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          placeholder="0.0"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(',', '.'))}
        />
        {onMax ? (
          <button type="button" className="max" onClick={onMax}>
            {t('common.max')}
          </button>
        ) : null}
        {chip ?? <TokenChip symbol={token} icon={icon} />}
      </div>
      {hint ? <div className="muted" style={{ fontSize: 12 }}>{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: ReactNode; onClose(): void; children: ReactNode }) {
  const { t } = useI18n();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="close" width={16} height={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Notice({ tone, children }: { tone?: 'warn' | 'error' | 'ok'; children: ReactNode }) {
  return <div className={`notice${tone ? ` ${tone}` : ''}`}>{children}</div>;
}

export function Rows({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <div className="rows">
      {items.map(([k, v], i) => (
        <div className="row" key={i}>
          <span>{k}</span>
          <span>{v}</span>
        </div>
      ))}
    </div>
  );
}
